/*
  Hero 3D scene — a small floating cluster of objects, standing in for
  "a considered collection of things" rather than one abstract shape.
  Sits to one side of the hero so it never fights the headline for
  attention. Loads Three.js from CDN, no build step required.

  Device handling: WebGL + Three.js (~150KB+ over the wire, plus a
  continuous requestAnimationFrame loop) only makes sense where there's
  room to see it and a device that can afford it. On phones the canvas
  is already dimmed to 50% behind the headline (see theme.css), so the
  full 3D scene is mostly wasted bandwidth and battery there. Instead of
  guessing from a user-agent string (unreliable and unmaintainable), we
  ask the platform directly what it's capable of:
    - matches the same 900px layout breakpoint the CSS uses (so JS and
      CSS never disagree about what counts as "mobile")
    - a fine pointer is available (mouse/trackpad) — the scene's parallax
      is pointer-driven and does nothing useful on touch
    - the person hasn't asked for reduced motion
  Anything that fails these checks gets a static CSS gradient instead —
  same palette, no JS, no network request, no animation loop. Everything
  re-evaluates live via matchMedia listeners, so resizing a window or
  rotating a tablet across the breakpoint swaps the right experience in
  or out without a page reload.
*/
(function () {
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  var desktopMQ = window.matchMedia('(min-width: 901px)');
  var finePointerMQ = window.matchMedia('(pointer: fine)');
  var motionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  function supportsHero3D() {
    return desktopMQ.matches && finePointerMQ.matches && !motionMQ.matches;
  }

  function setStatic(wrap, on) {
    wrap.classList.toggle('hero-3d__canvas-wrap--static', on);
  }

  // Soft, elevated palette pulled from the theme tokens — muted rather
  // than the saturated supplier-photo colors, so the cluster reads as
  // "curated" rather than "toy aisle."
  var PALETTE = [0xC98B6B, 0x8FA694, 0xA9825A, 0xD9C9A8, 0x6E7C87, 0xE7DCC9];

  function initHero3D(wrapId) {
    var wrap = document.getElementById(wrapId);
    if (!wrap || typeof THREE === 'undefined') return;

    var width = wrap.clientWidth;
    var height = wrap.clientHeight;

    var scene = new THREE.Scene();

    var camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
    camera.position.set(0, 0.4, 10);

    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    wrap.appendChild(renderer.domElement);

    /* Lighting — soft studio setup */
    var keyLight = new THREE.DirectionalLight(0xffffff, 1.15);
    keyLight.position.set(4, 6, 6);
    scene.add(keyLight);

    var fillLight = new THREE.DirectionalLight(0xC9A876, 0.5);
    fillLight.position.set(-6, -2, 3);
    scene.add(fillLight);

    var ambient = new THREE.AmbientLight(0xffffff, 0.45);
    scene.add(ambient);

    /* A group of objects, offset to the right of scene-center so it
       sits clear of the headline column on the left. Mixed geometry
       (sphere, rounded box via icosahedron, torus) to read as "a set
       of different small objects" rather than one thing repeated. */
    var group = new THREE.Group();
    group.position.x = 2.4;
    scene.add(group);

    var shapes = [
      { geo: new THREE.SphereGeometry(0.95, 48, 48), pos: [0, 0.6, 0], color: PALETTE[0] },
      { geo: new THREE.IcosahedronGeometry(0.7, 0), pos: [-1.7, -0.5, -0.6], color: PALETTE[1] },
      { geo: new THREE.SphereGeometry(0.55, 32, 32), pos: [1.6, -0.9, 0.4], color: PALETTE[2] },
      { geo: new THREE.TorusGeometry(0.5, 0.16, 20, 60), pos: [1.5, 1.3, -0.8], color: PALETTE[3] },
      { geo: new THREE.SphereGeometry(0.38, 32, 32), pos: [-1.4, 1.2, 0.5], color: PALETTE[4] }
    ];

    var meshes = shapes.map(function (s) {
      var mat = new THREE.MeshStandardMaterial({
        color: s.color,
        metalness: 0.25,
        roughness: 0.45
      });
      var mesh = new THREE.Mesh(s.geo, mat);
      mesh.position.set(s.pos[0], s.pos[1], s.pos[2]);
      mesh.userData.baseY = s.pos[1];
      mesh.userData.speed = 0.4 + Math.random() * 0.4;
      mesh.userData.offset = Math.random() * Math.PI * 2;
      group.add(mesh);
      return mesh;
    });

    /* Soft contact-shadow plane beneath the cluster for grounding */
    var shadowTex = (function () {
      var c = document.createElement('canvas');
      c.width = c.height = 256;
      var ctx = c.getContext('2d');
      var grad = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
      grad.addColorStop(0, 'rgba(0,0,0,0.28)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 256, 256);
      return new THREE.CanvasTexture(c);
    })();
    var shadowMat = new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false });
    var shadowPlane = new THREE.Mesh(new THREE.PlaneGeometry(5.5, 5.5), shadowMat);
    shadowPlane.rotation.x = -Math.PI / 2;
    shadowPlane.position.y = -2.1;
    group.add(shadowPlane);

    var mouseX = 0, mouseY = 0;
    window.addEventListener('mousemove', function (e) {
      mouseX = (e.clientX / window.innerWidth) - 0.5;
      mouseY = (e.clientY / window.innerHeight) - 0.5;
    });

    var clock = new THREE.Clock();

    function animate() {
      var t = clock.getElapsedTime();

      meshes.forEach(function (mesh) {
        mesh.position.y = mesh.userData.baseY + Math.sin(t * mesh.userData.speed + mesh.userData.offset) * 0.18;
        mesh.rotation.y = t * 0.2 + mesh.userData.offset;
        mesh.rotation.x = t * 0.12;
      });

      group.rotation.y = t * 0.08 + mouseX * 0.25;
      group.rotation.x = mouseY * 0.12;

      camera.position.x += (mouseX * 0.6 - camera.position.x) * 0.02;
      camera.lookAt(2.4, 0, 0);

      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    }
    animate();

    window.addEventListener('resize', function () {
      var w = wrap.clientWidth, h = wrap.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    var wraps = document.querySelectorAll('[data-hero-3d]');
    if (!wraps.length) return;

    var threeLoading = null;
    var initialized = {};

    function upgradeToThree() {
      wraps.forEach(function (wrap) { setStatic(wrap, false); });
      if (!threeLoading) {
        threeLoading = loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js');
      }
      threeLoading.then(function () {
        wraps.forEach(function (wrap) {
          if (!initialized[wrap.id]) {
            initialized[wrap.id] = true;
            initHero3D(wrap.id);
          }
        });
      });
    }

    function downgradeToStatic() {
      // Only meaningful before Three.js has ever initialized for a wrap —
      // once a WebGL context is running we leave it be rather than tear
      // down mid-session (e.g. briefly resizing a desktop window).
      wraps.forEach(function (wrap) {
        if (!initialized[wrap.id]) setStatic(wrap, true);
      });
    }

    function evaluate() {
      if (supportsHero3D()) {
        upgradeToThree();
      } else {
        downgradeToStatic();
      }
    }

    evaluate();

    // Re-check on breakpoint / pointer-type / motion-preference changes
    // (window resize crossing 900px, tablet rotation, connecting a mouse,
    // toggling OS-level reduced-motion) rather than only on first load.
    [desktopMQ, finePointerMQ, motionMQ].forEach(function (mq) {
      if (mq.addEventListener) mq.addEventListener('change', evaluate);
      else if (mq.addListener) mq.addListener(evaluate); // Safari <14 fallback
    });
  });
})();
